/* eslint-disable lingui/no-unlocalized-strings */
import { ENV_VARS } from '@env';
import {
  IS_LOCAL_DEV,
  IS_LOCAL_OR_CI_CYPRESS,
  isPRPreview,
} from '../utils/constants';
import { win } from '../utils/win';
import { CrossAppConfig, getEnvKeyFromHostname } from './base';
import { launchDarklyKeyMap } from './launchDarklyKeyMap';

/**
 * Connect-app config (Connect Carrier / Connect Customer).
 *
 * The connect apps derive their config entirely from the request hostname rather
 * than from the tenant registry, so this logic is self-contained and independent
 * of the registry-driven config builder.
 */

const devPort = ENV_VARS.VITE_API_PORT || '5321';
const localProtocol = win.location.protocol === 'https:' ? 'https' : 'http';

const getConnectCarrierLDKey = (base: string): string => {
  let key = launchDarklyKeyMap.production;
  if (base === 'dev') {
    key = launchDarklyKeyMap.internalDev;
  } else if (base === 'test') {
    key = launchDarklyKeyMap.internalTest;
  } else if (base.match(/uat/)) {
    key = launchDarklyKeyMap.preview;
  }
  return key;
};

const getConnectCustomerLDKey = (base: string): string => {
  let key = launchDarklyKeyMap.production;
  if (base === 'dev') {
    key = launchDarklyKeyMap.internalDev;
  } else if (base === 'test') {
    key = launchDarklyKeyMap.internalTest;
  } else if (base.match(/uat/)) {
    key = launchDarklyKeyMap.preview;
  }
  return key;
};

type ConnectCarrierConfig = CrossAppConfig & {
  uploadOrigin: string;
};

export const getConnectCarrierConfig = (
  hostname: string,
): ConnectCarrierConfig => {
  // connect-carrier.dev.hub.mastermindtms.com
  // connect-carrier.test.hub.mastermindtms.com
  // connect-carrier.uat-e.hub.mastermindtms.com
  // connect-carrier.uat-c.hub.mastermindtms.com
  // connect-carrier.hub.mastermindtms.com
  let base = hostname
    .replace('connect-carrier.', '')
    .replace('hub.mastermindtms.com', '')
    .replace('.', '');
  if (IS_LOCAL_DEV || isPRPreview(base)) {
    base = 'dev';
  }
  const launchDarklyKey = getConnectCarrierLDKey(base);
  if (base) {
    base = base + '.';
  }
  let apiEndpoint = `https://graphql.${base}hub.mastermindtms.com`;
  if (IS_LOCAL_OR_CI_CYPRESS) {
    apiEndpoint = `${localProtocol}://localhost:${devPort}`;
  }
  return {
    keycloakUri: `https://id.${base}hub.mastermindtms.com/auth`,
    keycloakClientId: `https://${base}hub.mastermindtms.com/carrier-connect`,
    keycloakRealm: `${base}hub.mastermindtms.com`,
    apiEndpoint,
    uploadOrigin: `https://carrier-connect.${base}hub.mastermindtms.com`,
    launchDarklyKey,
    envKey: getEnvKeyFromHostname(hostname),
  };
};

type ConnectCustomerConfig = CrossAppConfig & {
  uploadOrigin: string;
};

// ts-unused-exports:disable-next-line
export const getConnectCustomerConfig = (
  hostname: string,
): ConnectCustomerConfig => {
  // connect-customer.dev.hub.mastermindtms.com
  // connect-customer.test.hub.mastermindtms.com
  // connect-customer.uat-e.hub.mastermindtms.com
  // connect-customer.uat-c.hub.mastermindtms.com
  // connect-customer.hub.mastermindtms.com
  let base = hostname
    .replace('connect-customer.', '')
    .replace('hub.mastermindtms.com', '')
    .replace('.', '');
  if (IS_LOCAL_DEV || isPRPreview(base)) {
    base = 'dev';
  }
  const launchDarklyKey = getConnectCustomerLDKey(base);
  if (base) {
    base = base + '.';
  }
  let apiEndpoint = `https://graphql.${base}hub.mastermindtms.com`;
  if (IS_LOCAL_OR_CI_CYPRESS) {
    apiEndpoint = `${localProtocol}://localhost:${devPort}`;
  }
  return {
    keycloakUri: `https://id.${base}hub.mastermindtms.com/auth`,
    keycloakClientId: `https://${base}hub.mastermindtms.com/customer-connect`,
    keycloakRealm: `${base}hub.mastermindtms.com`,
    apiEndpoint,
    uploadOrigin: `https://customer-connect.${base}hub.mastermindtms.com`,
    launchDarklyKey,
    envKey: getEnvKeyFromHostname(hostname),
  };
};
